/*
 * bw-review-modal.js
 * Skins Judge.me's "Write a review" modal (injected at runtime) to the Betterway review flow design and turns
 * its negative-resolution help screen into our "Not wiping happy?" screen, where "Get it fixed" opens our own
 * dialog (another component listens for the 'bw:get-it-fixed' event).
 *
 * - Product pages only, loaded with defer. No dependencies.
 * - Idempotent: every change checks first, so the MutationObserver can call apply() as often as it likes.
 * - DOM is built with createElement/textContent only.
 * - Judge.me's own behavior is kept: we never remove its elements or handlers, we only hide some and add ours.
 * - The path follows the rating whenever it changes inside the form (help screen for a low rating, review steps for a
 *   high one), driven through Judge.me's own intro stars. See "rating changes" below.
 * - Review form steps go to window.bwTrack: Review Form Step Viewed, Review Submitted, Review Form Closed.
 */
(function () {
  'use strict';

  if (window.BWReviewModal) return;

  var P = 'jdgm-write-review-modal';
  var MODAL = '.' + P;
  var FONT_ID = 'bw-review-modal-font';
  var FONT_URL = 'https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght,SOFT,WONK@0,9..144,700,0..100,0..1;1,9..144,700,0..100,0..1&display=swap';
  var GUARANTEE_URL = '/pages/shipping-and-returns';
  var SVG_NS = 'http://www.w3.org/2000/svg';

  // Judge.me hooks for the review form step events (checked on Judge.me build 785, 15-10-2026). Recheck them when
  // Judge.me changes its modal.
  var JM = {
    current: '.' + P + '__page.current-page',      // the step on screen
    stepId: new RegExp(P + '__page--([a-z0-9-]+)'), // step id from the page classes
    page: '.' + P + '__page--',                     // + step id: that step's page
    stars: '.' + P + '__stars',                     // star widget, its hidden input holds the score
    rated: ['review-form-intro', 'negative-resolution', 'review-details'], // steps whose own stars hold the rating
    review: 'review-details',                       // its stars hold the rating on the steps after it
    help: 'negative-resolution',                    // the help screen (shown instead of review-details)
    intro: 'review-form-intro',                     // its star click is where Judge.me picks help or review
    thanks: 'thank-you',                            // built anew only when Judge.me accepts a review
    names: {
      'review-form-intro': 'stars',
      'negative-resolution': 'help',
      'review-details': 'review',
      'about-you': 'about_you',
      'share-media': 'photos',
      'custom-questions': 'questions',
      'confirm-email': 'confirm_email',
      'check-email': 'check_email',
      'thank-you': 'thanks'
    },
    settleMs: 300, // a step counts once it stayed current this long
    closeMs: 800   // no open modal this long means closed (Judge.me removes and rebuilds the modal on close)
  };

  var COPY = {
    getItFixed: 'Get it fixed',
    back: 'Back',
    titleA: 'Not wiping happy?',
    titleB1: "Let's ",
    titleFix: 'fix',
    titleB2: ' that.',
    covered: 'Every order is covered by our',
    guarantee: '100-Day Happy Wiping Guarantee',
    newTab: ' (opens in a new tab)',
    tellUs: "Tell us what went wrong and we'll make it right with a ",
    remedy: 'replacement or your money back',
    thanksTitle: 'Thanks for your review',
    otherProducts: 'Review other products in your order'
  };

  var bypassContact = false;
  var helpShownFor = null;
  var helpShownAt = 0;
  var observed = typeof WeakSet === 'function' ? new WeakSet() : null;
  var steps = { id: null, pending: null, rating: null, settle: 0, closing: 0 };
  var thanksSeen = typeof WeakSet === 'function' ? new WeakSet() : null;
  var route = { page: null, rating: null, timer: 0 };
  var reviewed = typeof WeakSet === 'function' ? new WeakSet() : null;
  var silentClick = false;
  var lastFocus = null; // last control the customer focused on the review step

  /* ---------- helpers ---------- */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function track(name, props) {
    try {
      if (typeof window.bwTrack === 'function') window.bwTrack(name, props || {});
    } catch (e) { /* analytics must never break the flow */ }
  }

  function urlParam(name) {
    var m = new RegExp('[?&]' + name + '=([^&#]*)').exec(window.location.href);
    if (!m) return null;
    try { return decodeURIComponent(m[1].replace(/\+/g, ' ')); } catch (e) { return m[1]; }
  }

  function productHandle() {
    var m = /\/products\/([^/?#]+)/.exec(window.location.pathname);
    return m ? decodeURIComponent(m[1]) : null;
  }

  function productTitle(modal) {
    var t = modal && modal.querySelector('.' + P + '__product-title');
    var text = t ? t.textContent.replace(/\s+/g, ' ').trim() : '';
    if (text) return text;
    var og = document.querySelector('meta[property="og:title"]');
    return og ? og.getAttribute('content') : document.title;
  }

  function starsValue(stars) {
    if (!stars) return null;
    var input = stars.querySelector('input');
    var v = input ? parseInt(input.value, 10) : NaN;
    if (v >= 1 && v <= 5) return v;
    var on = stars.querySelectorAll('.jdgm-star.jdgm--on').length;
    return on >= 1 && on <= 5 ? on : null;
  }

  function urlRating() {
    var p = parseInt(urlParam('judgeme_rating'), 10);
    return p >= 1 && p <= 5 ? p : null;
  }

  function helpRating(modal) {
    var page = modal && modal.querySelector('.' + P + '__page--negative-resolution');
    return starsValue(page && page.querySelector('.' + P + '__stars')) || urlRating();
  }

  function helpProps(modal) {
    return { rating: helpRating(modal), product: productTitle(modal), productHandle: productHandle() };
  }

  function isOpen(modal) {
    return !!modal && modal.style.display !== 'none' && window.getComputedStyle(modal).display !== 'none';
  }

  function loadFont() {
    if (document.getElementById(FONT_ID) || document.querySelector('link[href*="family=Fraunces"]')) return;
    var link = document.createElement('link');
    link.id = FONT_ID;
    link.rel = 'stylesheet';
    link.href = FONT_URL;
    document.head.appendChild(link);
  }

  function arrowIcon() {
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 16 14');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', 'M15 7H1M7 13L1 7L7 1');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '2');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(path);
    return svg;
  }

  function setText(node, text) {
    if (node && node.textContent.trim() !== text) node.textContent = text;
  }

  /* ---------- help screen (2B) ---------- */

  function buildTitle() {
    var h = el('h2', 'bw-jm-low__title');
    h.appendChild(document.createTextNode(COPY.titleA));
    h.appendChild(document.createElement('br'));
    h.appendChild(document.createTextNode(COPY.titleB1));
    h.appendChild(el('i', '', COPY.titleFix));
    h.appendChild(document.createTextNode(COPY.titleB2));
    return h;
  }

  function buildCopy() {
    var p = el('p', 'bw-jm-low__copy');
    var l1 = el('span', 'bw-jm-low__l1');
    l1.appendChild(document.createTextNode(COPY.covered));
    l1.appendChild(el('br', 'bw-jm-only-m'));
    l1.appendChild(document.createTextNode(' '));
    var a = el('a', '', COPY.guarantee);
    a.href = GUARANTEE_URL;
    a.target = '_blank';
    a.rel = 'noopener';
    a.appendChild(el('span', 'bw-jm-sr-only', COPY.newTab));
    l1.appendChild(a);
    l1.appendChild(document.createTextNode('.'));
    var l2 = el('span', 'bw-jm-low__l2');
    l2.appendChild(document.createTextNode(COPY.tellUs));
    l2.appendChild(el('b', '', COPY.remedy));
    l2.appendChild(document.createTextNode('.'));
    p.appendChild(l1);
    p.appendChild(document.createTextNode(' '));
    p.appendChild(l2);
    return p;
  }

  function enhanceHelpScreen(modal) {
    var page = modal.querySelector('.' + P + '__page--negative-resolution');
    if (!page) return;
    var content = page.querySelector('.' + P + '__negative-resolution-content');
    if (!content) return;

    if (!page.classList.contains('bw-jm-low')) page.classList.add('bw-jm-low');

    // Product name above the stars (Judge.me renders it when it knows the product; fill in if not).
    var header = page.querySelector('.' + P + '__page-header');
    if (header && !header.querySelector('.' + P + '__product-title')) {
      var name = el('p', 'bw-jm-pname', productTitle(null));
      header.insertBefore(name, header.firstChild);
    }

    if (!content.querySelector('.bw-jm-low__title')) {
      var anchor = content.querySelector('.' + P + '__negative-resolution-title') || content.firstChild;
      content.insertBefore(buildTitle(), anchor);
      content.insertBefore(buildCopy(), anchor);
    }

    setText(content.querySelector('.' + P + '__btn--contact-us'), COPY.getItFixed);

    // Judge.me's Back is an <a> without href (not reachable by keyboard): keep it hidden as the action target
    // and give people a real button that triggers it.
    var actions = content.querySelector('.' + P + '__negative-resolution-actions');
    var jmBack = actions && actions.querySelector('.' + P + '__btn--back');
    if (actions && jmBack && !actions.querySelector('.bw-jm-back')) {
      var back = el('button', 'bw-jm-back');
      back.type = 'button';
      back.appendChild(arrowIcon());
      back.appendChild(document.createTextNode(COPY.back));
      back.addEventListener('click', function () {
        track('Review Help Back Clicked', helpProps(modal));
        jmBack.click();
      });
      actions.insertBefore(back, actions.firstChild);
    }
    if (jmBack && jmBack.getAttribute('aria-hidden') !== 'true') jmBack.setAttribute('aria-hidden', 'true');
  }

  // One event per visit to the help screen. A visit ends only when another step becomes current, so Judge.me's
  // fade and re-renders never count twice. Judge.me also rebuilds the whole modal 100 to 250 ms after it first
  // opens (later on slow loads), which gives us a new help page element: when the element we counted was replaced
  // without the visit ending, it is the same visit (bounded to 10 s, so a later reopen still counts).
  function trackHelpShown(modal) {
    var page = modal.querySelector('.' + P + '__page--negative-resolution');
    if (!page) return;
    var current = modal.querySelector('.' + P + '__page.current-page');
    var showing = isOpen(modal) && current === page && page.style.display !== 'none';
    if (showing && helpShownFor !== page) {
      var rebuilt = !!helpShownFor && !helpShownFor.isConnected && Date.now() - helpShownAt < 10000;
      helpShownFor = page;
      helpShownAt = Date.now();
      if (!rebuilt) track('Review Help Shown', helpProps(modal));
    } else if (helpShownFor === page && current && current !== page) {
      helpShownFor = null;
    }
  }

  /* ---------- review form steps ---------- */

  function seenThanks(page) {
    return thanksSeen ? thanksSeen.has(page) : page.getAttribute('data-bw-seen') === '1';
  }

  function markThanks(page) {
    if (thanksSeen) thanksSeen.add(page);
    else page.setAttribute('data-bw-seen', '1');
  }

  function openModal() {
    var list = document.querySelectorAll(MODAL);
    for (var i = 0; i < list.length; i++) if (isOpen(list[i])) return list[i];
    return null;
  }

  // The step on screen: Judge.me's current page, unless it is hidden (help and review text swap with display).
  function currentPage(modal) {
    var page = modal.querySelector(JM.current);
    return page && page.style.display !== 'none' ? page : null;
  }

  function stepOf(page) {
    var m = page ? JM.stepId.exec(page.className) : null;
    return m ? m[1] : null;
  }

  function stepName(id) {
    return Object.prototype.hasOwnProperty.call(JM.names, id) ? JM.names[id] : id;
  }

  function stepRating(modal, id) {
    var own = JM.rated.indexOf(id) >= 0 ? modal.querySelector(JM.page + id + ' ' + JM.stars) : null;
    return starsValue(own) || starsValue(modal.querySelector(JM.page + JM.review + ' ' + JM.stars)) || steps.rating || urlRating();
  }

  function stepViewed(id, rating) {
    steps.id = id;
    steps.rating = rating;
    track('Review Form Step Viewed', { step: stepName(id), rating: rating });
  }

  // A step counts once it stayed on screen for JM.settleMs, so Judge.me's rebuilds and quick jumps never count twice.
  function settleStep() {
    steps.settle = 0;
    steps.pending = null;
    try {
      var modal = openModal();
      var page = modal && currentPage(modal);
      var id = stepOf(page);
      if (id && id !== steps.id) stepViewed(id, stepRating(modal, id));
    } catch (e) { /* analytics must never break the flow */ }
  }

  // Judge.me builds a new thank-you page only when its review POST succeeds (_submitCompleteReview), and shows it at
  // once. The one built with the modal, and one shown again by Back (from another product of the order), were seen
  // hidden first, so they never count as a submit. Counted right away: the visitor may move on within JM.settleMs.
  function reviewSubmitted(page) {
    markThanks(page);
    markReviewed(page.closest(MODAL));
    clearTimeout(steps.settle);
    steps.settle = 0;
    steps.pending = null;
    // Building that page resets every star widget: keep the rating of the steps before it.
    stepViewed(JM.thanks, steps.rating || urlRating());
    track('Review Submitted', { rating: steps.rating });
  }

  function trackFormStep(modal) {
    var thanks = modal.querySelector(JM.page + JM.thanks);
    if (thanks && !thanks.classList.contains('current-page')) markThanks(thanks);
    if (!isOpen(modal)) { formClosedCheck(); return; }
    var page = currentPage(modal);
    var id = stepOf(page);
    if (!id) return;
    clearTimeout(steps.closing);
    steps.closing = 0;
    if (id === JM.thanks && !seenThanks(page)) { reviewSubmitted(page); return; }
    if (id === (steps.pending || steps.id)) return;
    steps.pending = id;
    clearTimeout(steps.settle);
    steps.settle = setTimeout(settleStep, JM.settleMs);
  }

  // Closed: no Judge.me modal open for JM.closeMs. Not after the thank-you step: that review was sent.
  function formClosedCheck() {
    if (!steps.id && !steps.pending) return;
    if (openModal()) { clearTimeout(steps.closing); steps.closing = 0; return; }
    if (steps.closing) return;
    steps.closing = setTimeout(function () {
      steps.closing = 0;
      try {
        if (openModal()) return;
        clearTimeout(steps.settle);
        if (steps.id && steps.id !== JM.thanks) track('Review Form Closed', { last_step: stepName(steps.id), rating: steps.rating });
        steps.id = steps.pending = steps.rating = null;
        steps.settle = 0;
      } catch (e) { /* analytics must never break the flow */ }
    }, JM.closeMs);
  }

  /* ---------- rating changes ---------- */

  // Judge.me picks the help screen or the review steps only when a star is picked on its intro step (rating at or
  // below jdgmSettings.new_review_flow_help_screen_rating_threshold: help). The stars on the review step and on the help
  // screen only change the rating, so a 4 changed to 2 never saw the help screen and a 2 changed to 4 stayed on it.
  // To follow the current rating we click the hidden intro star of the new rating: Judge.me's own handler then shows
  // the help screen or the review step, the same thing it does itself after loading the reviewer data
  // (_prefillFormData). Rules:
  // - help screen, rating goes above the threshold: review steps;
  // - review step, rating goes from above the threshold to at or below it: help screen, which still offers
  //   "Leave a review" (a change inside the low range after that keeps the review step);
  // - a change counts only while the same step stays on screen, so arriving on a step never reroutes;
  // - not after a review was sent in this modal: "review other products" and the store review never had a help screen.
  // Judge.me also applies the rating again each time its reviewer data loads, several times in the first seconds
  // after the page opens (seen up to about 5 s). With a low rating that flips the review step back to the help screen,
  // even after the customer picked "Leave a review" (this happened before our change too). A flip from the review step
  // to the help screen with the same rating and no action of ours can only be that, so we take the review step again
  // through Judge.me's own "Leave a review" button, before the browser paints and without tracking it.

  function markReviewed(modal) {
    if (!modal) return;
    if (reviewed) reviewed.add(modal);
    else modal.setAttribute('data-bw-reviewed', '1');
  }

  function wasReviewed(modal) {
    return reviewed ? reviewed.has(modal) : modal.getAttribute('data-bw-reviewed') === '1';
  }

  function helpThreshold() {
    var t = parseInt(window.jdgmSettings && window.jdgmSettings.new_review_flow_help_screen_rating_threshold, 10);
    return t >= 1 && t <= 5 ? t : 3;
  }

  function introStar(modal, rating) {
    return modal.querySelector(JM.page + JM.intro + ' ' + JM.stars + ' .jdgm-star[data-alt="' + rating + '"]');
  }

  // Our own clicks on Judge.me's controls: not tracked as the customer's.
  function quietClick(node) {
    silentClick = true;
    try { node.click(); } finally { silentClick = false; }
  }

  // Remember where the customer is, so a step change we caused is not taken for one of Judge.me's.
  function setRoute(modal) {
    var page = currentPage(modal);
    var id = stepOf(page);
    route.page = id === JM.review || id === JM.help ? page : null;
    route.rating = route.page ? starsValue(page.querySelector(JM.stars)) : null;
  }

  // Judge.me moves focus to the first control of the modal (the close button) when the step swaps: put it back on
  // the same kind of control the customer was on, when that control is on screen.
  function restoreFocus(modal, before) {
    var now = currentPage(modal);
    if (!before || !now || !modal.contains(before) || document.activeElement === before) return;
    var star = before.closest('.jdgm-star');
    var target = now.contains(before) ? before
      : star ? now.querySelector(JM.stars + ' .jdgm-star[data-alt="' + star.getAttribute('data-alt') + '"]') : null;
    if (target && target.getClientRects().length) target.focus({ preventScroll: true });
  }

  function reroute(modal, page, rating) {
    route.timer = 0;
    try {
      if (!isOpen(modal) || currentPage(modal) !== page) return; // moved on meanwhile
      if (starsValue(page.querySelector(JM.stars)) !== rating) return; // changed again: that change decides
      var star = introStar(modal, rating);
      if (!star) return;
      var before = page.contains(document.activeElement) ? document.activeElement : null;
      quietClick(star);
      setRoute(modal);
      restoreFocus(modal, before);
    } catch (e) { /* never break Judge.me's own flow */ }
  }

  function keepReviewStep(modal, help) {
    var leave = help.querySelector('.' + P + '__btn--leave-review');
    if (!leave) return;
    var before = lastFocus; // Judge.me focuses the first star while it swaps back, which would overwrite it
    quietClick(leave);
    setRoute(modal);
    restoreFocus(modal, before);
  }

  function followRating(modal) {
    if (!isOpen(modal)) return;
    var page = currentPage(modal);
    var id = stepOf(page);
    if (id !== JM.review && id !== JM.help) { route.page = null; return; }
    var rating = starsValue(page.querySelector(JM.stars));
    if (route.page !== page) {
      var from = route.page;
      var fromRating = route.rating;
      route.page = page;
      route.rating = rating;
      // Review step straight to the help screen, same rating, nothing of ours pending (route.timer): Judge.me's flip.
      var flip = id === JM.help && !!rating && rating === fromRating && !route.timer &&
        !!from && stepOf(from) === JM.review && modal.contains(from) && !wasReviewed(modal);
      if (flip) keepReviewStep(modal, page);
      return;
    }
    if (!rating || rating === route.rating) return;
    var before = route.rating;
    route.rating = rating;
    if (steps.id) steps.rating = rating; // Review Form Closed reports the rating on screen
    if (!modal.querySelector(JM.page + JM.help) || wasReviewed(modal)) return;
    var t = helpThreshold();
    var go = id === JM.help ? rating > t : rating <= t && !(before && before <= t);
    if (!go) return;
    clearTimeout(route.timer);
    // After Judge.me's own handlers for this click have run.
    route.timer = setTimeout(function () { reroute(modal, page, rating); }, 0);
  }

  /* ---------- other steps ---------- */

  function enhanceThankYou(modal) {
    var page = modal.querySelector('.' + P + '__page--thank-you');
    if (!page) return;
    setText(page.querySelector('.' + P + '__form--thank-you > .' + P + '__title:not(.' + P + '__other-products-title)'), COPY.thanksTitle);
    setText(page.querySelector('.' + P + '__other-products-title'), COPY.otherProducts);
  }

  // Judge.me hides its close button after step 1; the design keeps it on every step.
  function keepCloseButton(modal) {
    var btn = modal.querySelector('.' + P + '__close-btn');
    if (!btn) return;
    if (btn.getAttribute('aria-hidden') === 'true') btn.removeAttribute('aria-hidden');
    if (btn.getAttribute('tabindex') === '-1') btn.removeAttribute('tabindex');
  }

  function apply(modal) {
    if (!modal || !modal.isConnected) {
      // Judge.me removes the modal at the end of its close: the step events need to know.
      try { formClosedCheck(); } catch (e) { /* analytics must never break the flow */ }
      return;
    }
    keepCloseButton(modal);
    enhanceHelpScreen(modal);
    enhanceThankYou(modal);
    if (isOpen(modal)) loadFont();
    // First: a flip back to the help screen is undone here, so the step events below never see it.
    try { followRating(modal); } catch (e) { /* never break Judge.me's own flow */ }
    trackHelpShown(modal);
    try { trackFormStep(modal); } catch (e) { /* analytics must never break the flow */ }
  }

  function watch(modal) {
    if (observed) {
      if (observed.has(modal)) return;
      observed.add(modal);
    } else {
      if (modal.getAttribute('data-bw-watched')) return;
      modal.setAttribute('data-bw-watched', '1');
    }
    apply(modal);
    new MutationObserver(function () { apply(modal); }).observe(modal, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden', 'tabindex']
    });
  }

  function scan() {
    var list = document.querySelectorAll(MODAL);
    for (var i = 0; i < list.length; i++) watch(list[i]);
    try { formClosedCheck(); } catch (e) { /* analytics must never break the flow */ }
  }

  /* ---------- Contact us -> Get it fixed ---------- */

  function onClick(e) {
    var target = e.target && e.target.closest ? e.target : null;
    if (!target || silentClick) return;

    var contact = target.closest('.' + P + '__btn--contact-us');
    if (contact) {
      if (bypassContact) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var modal = contact.closest(MODAL);
      var props = helpProps(modal);
      track('Review Help Get It Fixed Clicked', props);
      var detail = {
        rating: props.rating,
        productHandle: props.productHandle,
        productTitle: props.product,
        productUrl: window.location.pathname,
        source: 'judgeme-help',
        handled: false
      };
      try {
        document.dispatchEvent(new CustomEvent('bw:get-it-fixed', { detail: detail }));
      } catch (err) { /* a broken listener must not leave the button dead */ }
      if (!detail.handled) {
        // Nobody took it: let Judge.me do what it always did.
        bypassContact = true;
        try { contact.click(); } finally { bypassContact = false; }
      }
      return;
    }

    var leave = target.closest('.' + P + '__btn--leave-review');
    if (leave) track('Review Help Leave Review Clicked', helpProps(leave.closest(MODAL)));
  }

  /* ---------- public API ---------- */

  window.BWReviewModal = {
    // Closes Judge.me's modal the way its own close button does. Returns true when a modal was open.
    close: function () {
      var list = document.querySelectorAll(MODAL);
      for (var i = 0; i < list.length; i++) {
        if (!isOpen(list[i])) continue;
        var btn = list[i].querySelector('.' + P + '__close-btn');
        if (btn) { btn.click(); return true; }
      }
      return false;
    }
  };

  /* ---------- start ---------- */

  document.addEventListener('click', onClick, true);
  document.addEventListener('focusin', function (e) {
    var t = e.target;
    if (t && t.closest && t.closest(JM.page + JM.review)) lastFocus = t;
  }, true);

  function start() {
    scan();
    // Judge.me prepends the modal to <body> and rebuilds it after every close.
    new MutationObserver(scan).observe(document.body, { childList: true });
  }

  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start);
})();
